/**
 * Sinh bảng route từ cây thư mục `src/app`, giữ đúng quy ước của App Router.
 *
 * Vì sao sinh tự động thay vì gõ tay hàng trăm route: cây thư mục đã là nguồn chân lý
 * và đang đúng. Chép tay sang một mảng cấu hình chỉ tạo thêm một bản sao để
 * lệch nhau — thêm trang mới mà quên khai báo thì lỗi chỉ lộ ra lúc chạy.
 * Ngoài ra test "menu ↔ cây route" hiện có vẫn đúng vì đường dẫn file không đổi.
 *
 * Quy tắc chuyển đổi (khớp App Router):
 *   app/dashboard/orders/page.tsx        → /dashboard/orders
 *   app/dashboard/orders/[id]/page.tsx   → /dashboard/orders/:id
 *   app/(auth)/login/page.tsx            → /login          (route group bị bỏ)
 *   app/dashboard/layout.tsx             → route cha có <Outlet/>
 *   app/**\/_components/**               → không bao giờ là route
 *
 * Repo không có catch-all `[...slug]`, optional catch-all, parallel route hay
 * intercepting route nào — đã kiểm tra khi audit. Nếu sau này thêm, phải bổ
 * sung quy tắc ở đây.
 */

import type { ComponentType } from 'react';

/** Module một file route xuất ra. */
interface RouteModule {
  default: ComponentType;
}

type Loader = () => Promise<RouteModule>;

export interface RouteNode {
  /** Đoạn đường dẫn tương đối so với route cha ('' nghĩa là index). */
  segment: string;
  layout?: Loader;
  page?: Loader;
  children: Map<string, RouteNode>;
}

/**
 * Đổi đường dẫn file thành các đoạn URL.
 * Trả `null` khi file không phải route (nằm trong thư mục `_*`).
 */
export function filePathToSegments(relPath: string): string[] | null {
  const parts = relPath.split('/');
  // Bỏ tên file (page.tsx / layout.tsx)
  parts.pop();

  const segments: string[] = [];
  for (const part of parts) {
    // `_components`, `_hooks`, `_lib`: thư mục hỗ trợ, không phải route.
    if (part.startsWith('_')) return null;
    // `(auth)`: route group — có mặt trong cây thư mục nhưng không vào URL.
    if (part.startsWith('(') && part.endsWith(')')) continue;
    // `[id]` → `:id`
    if (part.startsWith('[') && part.endsWith(']')) {
      segments.push(':' + part.slice(1, -1));
      continue;
    }
    segments.push(part);
  }
  return segments;
}

/**
 * Dựng cây route từ hai map của `import.meta.glob`.
 *
 * `pages`/`layouts` có khoá là đường dẫn tương đối từ `src/app`, ví dụ
 * `dashboard/orders/[id]/page.tsx`.
 */
export function buildRouteTree(
  pages: Record<string, Loader>,
  layouts: Record<string, Loader>,
): RouteNode {
  const root: RouteNode = { segment: '', children: new Map() };

  const nodeAt = (segments: string[]): RouteNode => {
    let node = root;
    for (const seg of segments) {
      let next = node.children.get(seg);
      if (!next) {
        next = { segment: seg, children: new Map() };
        node.children.set(seg, next);
      }
      node = next;
    }
    return node;
  };

  // Layout trước, để route cha luôn tồn tại khi gắn page vào.
  for (const [rel, loader] of Object.entries(layouts)) {
    const segments = filePathToSegments(rel);
    if (!segments) continue;
    nodeAt(segments).layout = loader;
  }

  for (const [rel, loader] of Object.entries(pages)) {
    const segments = filePathToSegments(rel);
    if (!segments) continue;
    nodeAt(segments).page = loader;
  }

  return root;
}

/** Đường dẫn URL đầy đủ của mọi page trong cây — dùng cho test và kiểm kê. */
export function collectPaths(node: RouteNode, prefix = ''): string[] {
  const here = prefix + (node.segment ? '/' + node.segment : '');
  const out: string[] = [];
  if (node.page) out.push(here === '' ? '/' : here);
  for (const child of node.children.values()) out.push(...collectPaths(child, here));
  return out.sort();
}
